import React, {useEffect,useState} from 'react'
import {Pressable, StyleSheet,Text, View} from 'react-native'
import {msg,Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {isWeb} from '#/platform/detection'
import {atoms as a, useTheme} from '#/alf'

// Cookie management utilities
const COOKIE_NAME = 'age_verified'
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60 // 365 days in seconds

function setCookie(name: string, value: string, maxAge?: number) {
  if (!isWeb) return
  // If maxAge is provided, set a persistent cookie. Otherwise, set a session cookie.
  const cookieString = maxAge
    ? `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`
    : `${name}=${value}; path=/; SameSite=Lax`
  document.cookie = cookieString
}

function getCookie(name: string): string | null {
  if (!isWeb) return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? match[2] : null
}

interface AgeVerificationProps {
  onVerified: () => void
}

export function AgeVerification({onVerified}: AgeVerificationProps) {
  const t = useTheme()
  const {_} = useLingui()
  const [rememberMe, setRememberMe] = useState(false)

  // Check if already verified via cookie
  useEffect(() => {
    if (getCookie(COOKIE_NAME) === 'true') {
      onVerified()
    }
  }, [onVerified])

  const handleYesClick = () => {
    if (rememberMe) {
      // Set 365-day persistent cookie
      setCookie(COOKIE_NAME, 'true', COOKIE_MAX_AGE)
    } else {
      // Set session cookie (expires when browser closes)
      setCookie(COOKIE_NAME, 'true')
    }
    onVerified()
  }

  const handleNoClick = () => {
    // Privacy-preserving redirect using noreferrer
    const anchor = document.createElement('a')
    anchor.href = 'https://start.duckduckgo.com'
    anchor.rel = 'noreferrer noopener'
    anchor.target = '_self'
    
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  const toggleRememberMe = () => {
    setRememberMe(!rememberMe)
  }

  if (!isWeb) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}>
      <View
        style={[
          styles.modal,
          t.atoms.bg,
          {
            borderColor: t.palette.contrast_200,
          },
        ]}>
        {/* Question */}
        <Text style={[styles.question, t.atoms.text, a.font_bold, a.text_2xl]}>
          <Trans>Are you over 18 years of age?</Trans>
        </Text>

        {/* Buttons Container */}
        <View style={styles.buttonsContainer}>
          <Pressable
            onPress={handleYesClick}
            style={[
              styles.button,
              styles.yesButton,
              {backgroundColor: t.palette.primary_500},
            ]}
            accessibilityRole="button"
            accessibilityLabel={_(msg`Yes, I am over 18`)}>
            <Text style={[styles.buttonText, {color: '#fff'}]}>
              <Trans>Yes</Trans>
            </Text>
          </Pressable>

          <Pressable
            onPress={handleNoClick}
            style={[
              styles.button,
              styles.noButton,
              {
                backgroundColor: t.palette.contrast_100,
                borderColor: t.palette.contrast_300,
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel={_(msg`No, I am not over 18`)}>
            <Text style={[styles.buttonText, t.atoms.text]}>
              <Trans>No</Trans>
            </Text>
          </Pressable>
        </View>

        {/* Remember Me Checkbox */}
        <Pressable
          onPress={toggleRememberMe}
          style={styles.checkboxContainer}
          accessibilityRole="checkbox"
          accessibilityState={{checked: rememberMe}}
          accessibilityLabel={_(msg`Remember me for 365 days`)}>
          <View
            style={[
              styles.checkbox,
              {
                borderColor: t.palette.contrast_400,
                backgroundColor: rememberMe
                  ? t.palette.primary_500
                  : 'transparent',
              },
            ]}>
            {rememberMe && (
              <Text style={styles.checkmark}>✓</Text>
            )}
          </View>
          <Text style={[styles.checkboxLabel, t.atoms.text]}>
            <Trans>Remember me</Trans>
          </Text>
        </Pressable>
      </View>
    </div>
  )
}

const styles = StyleSheet.create({
  modal: {
    maxWidth: 500,
    width: '90%',
    padding: 40,
    borderRadius: 16,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    alignItems: 'center',
  },
  question: {
    marginBottom: 32,
    textAlign: 'center',
    lineHeight: 32,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 56,
  },
  yesButton: {
    // Primary button styles handled via backgroundColor
  },
  noButton: {
    borderWidth: 1,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  checkboxLabel: {
    fontSize: 16,
  },
})
